import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import {
  Loader2,
  Wrench,
  AlertTriangle,
  Clock,
  Plus,
  Settings,
  History,
  CheckCircle2,
  XCircle,
  Thermometer,
  ClipboardCheck,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MachineManagement() {
  const [loading, setLoading] = useState(true);
  const [machines, setMachines] = useState([]);
  const [maintenanceConfigs, setMaintenanceConfigs] = useState([]);
  const [maintenanceRecords, setMaintenanceRecords] = useState([]);
  const [breakdowns, setBreakdowns] = useState([]);
  const [maintenanceAlerts, setMaintenanceAlerts] = useState([]);
  const [checklistItems, setChecklistItems] = useState([]);
  const [checklistExecutions, setChecklistExecutions] = useState([]);
  const [temperatureRecords, setTemperatureRecords] = useState([]);
  
  // Dialogs
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [recordDialogOpen, setRecordDialogOpen] = useState(false);
  const [breakdownDialogOpen, setBreakdownDialogOpen] = useState(false);
  const [checklistDialogOpen, setChecklistDialogOpen] = useState(false);
  const [temperatureDialogOpen, setTemperatureDialogOpen] = useState(false);
  
  const [selectedMachine, setSelectedMachine] = useState(null);
  
  // Forms
  const [configForm, setConfigForm] = useState({
    interval_hours: 500,
    warning_threshold: 0.9,
    description: "",
    tasks: []
  });
  
  const [recordForm, setRecordForm] = useState({
    maintenance_type: "preventive",
    description: "",
    tasks_completed: [],
    parts_replaced: [],
    duration_minutes: 0,
    cost: 0,
    notes: ""
  });
  
  const [breakdownForm, setBreakdownForm] = useState({
    description: "",
    breakdown_type: "mechanical",
    severity: "medium",
    symptoms: [],
    affected_components: []
  });
  
  const [temperatureForm, setTemperatureForm] = useState({
    mold_zone_fixed: "",
    mold_zone_mobile: "",
    cylinder_zone_1: "",
    cylinder_zone_2: "",
    cylinder_zone_3: "",
    cylinder_zone_4: "",
    nozzle_temp: "",
    oil_temp: "",
    notes: ""
  });

  const fetchData = useCallback(async () => {
    try {
      const [machinesRes, configsRes, recordsRes, breakdownsRes, alertsRes, checklistItemsRes, checklistExecsRes, tempRes] = await Promise.all([
        axios.get(`${API}/machines`),
        axios.get(`${API}/machine-maintenance/config`),
        axios.get(`${API}/machine-maintenance/records`),
        axios.get(`${API}/breakdowns`),
        axios.get(`${API}/machine-maintenance/alerts`),
        axios.get(`${API}/startup-checklist/items`),
        axios.get(`${API}/startup-checklist/executions`),
        axios.get(`${API}/temperature-records?limit=50`),
      ]);
      
      setMachines(machinesRes.data);
      setMaintenanceConfigs(configsRes.data);
      setMaintenanceRecords(recordsRes.data);
      setBreakdowns(breakdownsRes.data);
      setMaintenanceAlerts(alertsRes.data);
      setChecklistItems(checklistItemsRes.data);
      setChecklistExecutions(checklistExecsRes.data);
      setTemperatureRecords(tempRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getConfigForMachine = (machineId) => {
    return maintenanceConfigs.find(c => c.machine_id === machineId);
  };

  const getMachineMaintenanceStatus = (machineId) => {
    const alert = maintenanceAlerts.find(a => a.machine_id === machineId);
    if (!alert) return { status: "ok", percentage: 0 };
    return { status: alert.status, percentage: alert.percentage };
  };

  const handleSaveConfig = async () => {
    try {
      await axios.post(`${API}/machine-maintenance/config`, {
        machine_id: selectedMachine.id,
        maintenance_type: "preventive",
        ...configForm,
        current_hours: getConfigForMachine(selectedMachine.id)?.current_hours || 0,
        last_maintenance_hours: getConfigForMachine(selectedMachine.id)?.last_maintenance_hours || 0
      });
      toast.success("Configuração guardada!");
      setConfigDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Erro ao guardar configuração");
    }
  };

  const handleSaveRecord = async () => {
    try {
      await axios.post(`${API}/machine-maintenance/record`, {
        machine_id: selectedMachine.id,
        ...recordForm
      });
      toast.success("Manutenção registada!");
      setRecordDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Erro ao registar manutenção");
    }
  };

  const handleReportBreakdown = async () => {
    try {
      await axios.post(`${API}/breakdowns`, {
        machine_id: selectedMachine.id,
        ...breakdownForm
      });
      toast.success("Avaria reportada!");
      setBreakdownDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Erro ao reportar avaria");
    }
  };

  const handleResolveBreakdown = async (breakdownId) => {
    const solution = prompt("Descreva a solução aplicada:");
    if (!solution) return;
    
    try {
      await axios.put(`${API}/breakdowns/${breakdownId}`, {
        status: "resolved",
        solution: solution
      });
      toast.success("Avaria resolvida!");
      fetchData();
    } catch (error) {
      toast.error("Erro ao resolver avaria");
    }
  };

  const handleSaveTemperature = async () => {
    try {
      const data = {
        machine_id: selectedMachine.id,
        mold_zone_fixed: temperatureForm.mold_zone_fixed ? parseFloat(temperatureForm.mold_zone_fixed) : null,
        mold_zone_mobile: temperatureForm.mold_zone_mobile ? parseFloat(temperatureForm.mold_zone_mobile) : null,
        cylinder_zone_1: temperatureForm.cylinder_zone_1 ? parseFloat(temperatureForm.cylinder_zone_1) : null,
        cylinder_zone_2: temperatureForm.cylinder_zone_2 ? parseFloat(temperatureForm.cylinder_zone_2) : null,
        cylinder_zone_3: temperatureForm.cylinder_zone_3 ? parseFloat(temperatureForm.cylinder_zone_3) : null,
        cylinder_zone_4: temperatureForm.cylinder_zone_4 ? parseFloat(temperatureForm.cylinder_zone_4) : null,
        nozzle_temp: temperatureForm.nozzle_temp ? parseFloat(temperatureForm.nozzle_temp) : null,
        oil_temp: temperatureForm.oil_temp ? parseFloat(temperatureForm.oil_temp) : null,
        notes: temperatureForm.notes
      };
      
      await axios.post(`${API}/temperature-records`, data);
      toast.success("Temperaturas registadas!");
      setTemperatureDialogOpen(false);
      setTemperatureForm({
        mold_zone_fixed: "", mold_zone_mobile: "", cylinder_zone_1: "", cylinder_zone_2: "",
        cylinder_zone_3: "", cylinder_zone_4: "", nozzle_temp: "", oil_temp: "", notes: ""
      });
      fetchData();
    } catch (error) {
      toast.error("Erro ao registar temperaturas");
    }
  };

  const handleUpdateHours = async (machineId) => {
    const hours = prompt("Introduza as horas actuais de funcionamento:");
    if (!hours || isNaN(hours)) return;
    
    try {
      await axios.post(`${API}/machine-maintenance/update-hours/${machineId}`, null, {
        params: { hours: parseInt(hours) }
      });
      toast.success("Horas atualizadas!");
      fetchData();
    } catch (error) {
      toast.error("Erro ao atualizar horas");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="machine-management-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-chivo text-3xl md:text-4xl font-bold tracking-tight text-foreground">
            Gestão de Máquinas
          </h1>
          <p className="text-muted-foreground mt-1">
            Manutenção preventiva, avarias e temperaturas
          </p>
        </div>
      </div>

      {/* Maintenance Alerts */}
      {maintenanceAlerts.length > 0 && (
        <Card className="border-amber-300 bg-amber-50">
          <CardHeader>
            <CardTitle className="font-chivo flex items-center gap-2 text-amber-800">
              <AlertTriangle className="h-5 w-5" />
              Alertas de Manutenção ({maintenanceAlerts.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3">
              {maintenanceAlerts.map((alert) => (
                <div 
                  key={alert.machine_id}
                  className={`p-3 rounded-lg border ${
                    alert.status === 'critical' ? 'bg-red-100 border-red-300' : 'bg-amber-100 border-amber-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-bold">{alert.machine_code}</span>
                      <span className="text-sm ml-2">
                        {alert.hours_since_maintenance}h desde última manutenção (limite: {alert.interval_hours}h)
                      </span>
                    </div>
                    <Badge className={alert.status === 'critical' ? 'bg-red-500' : 'bg-amber-500'}>
                      {alert.percentage}%
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="maintenance" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="maintenance">
            <Wrench className="h-4 w-4 mr-2" />
            Manutenção
          </TabsTrigger>
          <TabsTrigger value="breakdowns">
            <AlertTriangle className="h-4 w-4 mr-2" />
            Avarias ({breakdowns.filter(b => b.status !== 'closed').length})
          </TabsTrigger>
          <TabsTrigger value="temperatures">
            <Thermometer className="h-4 w-4 mr-2" />
            Temperaturas
          </TabsTrigger>
          <TabsTrigger value="checklist">
            <ClipboardCheck className="h-4 w-4 mr-2" />
            Checklist Arranque
          </TabsTrigger>
        </TabsList>

        {/* Maintenance Tab */}
        <TabsContent value="maintenance">
          <Card>
            <CardHeader>
              <CardTitle className="font-chivo">Manutenção Preventiva por Máquina</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Máquina</TableHead>
                    <TableHead>Intervalo</TableHead>
                    <TableHead>Horas Atuais</TableHead>
                    <TableHead>Última Manutenção</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {machines.map((machine) => {
                    const config = getConfigForMachine(machine.id);
                    const status = getMachineMaintenanceStatus(machine.id);
                    
                    return (
                      <TableRow key={machine.id}>
                        <TableCell>
                          <div className="font-bold">{machine.code}</div>
                          <div className="text-xs text-muted-foreground">{machine.description}</div>
                        </TableCell>
                        <TableCell>
                          {config ? `${config.interval_hours}h` : '-'}
                        </TableCell>
                        <TableCell>
                          {config ? `${config.current_hours}h` : '-'}
                        </TableCell>
                        <TableCell>
                          {config?.last_maintenance_at 
                            ? new Date(config.last_maintenance_at).toLocaleDateString('pt-PT')
                            : '-'}
                        </TableCell>
                        <TableCell>
                          {status.percentage > 0 ? (
                            <Badge className={
                              status.status === 'critical' ? 'bg-red-500' :
                              status.status === 'warning' ? 'bg-amber-500' :
                              'bg-green-500'
                            }>
                              {status.percentage}%
                            </Badge>
                          ) : (
                            <Badge variant="outline">OK</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedMachine(machine);
                                const existingConfig = getConfigForMachine(machine.id);
                                if (existingConfig) {
                                  setConfigForm({
                                    interval_hours: existingConfig.interval_hours,
                                    warning_threshold: existingConfig.warning_threshold,
                                    description: existingConfig.description || "",
                                    tasks: existingConfig.tasks || []
                                  });
                                }
                                setConfigDialogOpen(true);
                              }}
                            >
                              <Settings className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleUpdateHours(machine.id)}
                            >
                              <Clock className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => {
                                setSelectedMachine(machine);
                                setRecordForm({
                                  maintenance_type: "preventive",
                                  description: "",
                                  tasks_completed: [],
                                  parts_replaced: [],
                                  duration_minutes: 0,
                                  cost: 0,
                                  notes: ""
                                });
                                setRecordDialogOpen(true);
                              }}
                            >
                              <Plus className="h-4 w-4 mr-1" />
                              Registar
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Maintenance History */}
          <Card className="mt-4">
            <CardHeader>
              <CardTitle className="font-chivo flex items-center gap-2">
                <History className="h-5 w-5" />
                Histórico de Manutenções
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data</TableHead>
                    <TableHead>Máquina</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Descrição</TableHead>
                    <TableHead>Duração</TableHead>
                    <TableHead>Custo</TableHead>
                    <TableHead>Técnico</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {maintenanceRecords.slice(0, 20).map((record) => (
                    <TableRow key={record.id}>
                      <TableCell>
                        {new Date(record.performed_at).toLocaleDateString('pt-PT')}
                      </TableCell>
                      <TableCell className="font-mono">{record.machine_code}</TableCell>
                      <TableCell>
                        <Badge variant={record.maintenance_type === 'preventive' ? 'default' : 'destructive'}>
                          {record.maintenance_type === 'preventive' ? 'Preventiva' :
                           record.maintenance_type === 'corrective' ? 'Corretiva' : 'Emergência'}
                        </Badge>
                      </TableCell>
                      <TableCell>{record.description}</TableCell>
                      <TableCell>{record.duration_minutes} min</TableCell>
                      <TableCell>€{record.cost?.toFixed(2)}</TableCell>
                      <TableCell>{record.performed_by_name}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Breakdowns Tab */}
        <TabsContent value="breakdowns">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-chivo">Histórico de Avarias</CardTitle>
              <Button
                onClick={() => {
                  setSelectedMachine(null);
                  setBreakdownForm({
                    description: "",
                    breakdown_type: "mechanical",
                    severity: "medium",
                    symptoms: [],
                    affected_components: []
                  });
                  setBreakdownDialogOpen(true);
                }}
              >
                <Plus className="h-4 w-4 mr-2" />
                Reportar Avaria
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data</TableHead>
                    <TableHead>Máquina</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Severidade</TableHead>
                    <TableHead>Descrição</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {breakdowns.map((breakdown) => (
                    <TableRow key={breakdown.id}>
                      <TableCell>
                        {new Date(breakdown.reported_at).toLocaleDateString('pt-PT')}
                      </TableCell>
                      <TableCell className="font-mono">{breakdown.machine_code}</TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {breakdown.breakdown_type === 'mechanical' ? 'Mecânica' :
                           breakdown.breakdown_type === 'electrical' ? 'Elétrica' :
                           breakdown.breakdown_type === 'hydraulic' ? 'Hidráulica' :
                           breakdown.breakdown_type === 'pneumatic' ? 'Pneumática' :
                           breakdown.breakdown_type === 'software' ? 'Software' : 'Outro'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={
                          breakdown.severity === 'critical' ? 'bg-red-500' :
                          breakdown.severity === 'high' ? 'bg-orange-500' :
                          breakdown.severity === 'medium' ? 'bg-amber-500' :
                          'bg-green-500'
                        }>
                          {breakdown.severity === 'critical' ? 'Crítica' :
                           breakdown.severity === 'high' ? 'Alta' :
                           breakdown.severity === 'medium' ? 'Média' : 'Baixa'}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-xs truncate">{breakdown.description}</TableCell>
                      <TableCell>
                        <Badge variant={
                          breakdown.status === 'resolved' ? 'default' :
                          breakdown.status === 'in_progress' ? 'secondary' : 'destructive'
                        }>
                          {breakdown.status === 'resolved' ? 'Resolvida' :
                           breakdown.status === 'in_progress' ? 'Em Progresso' :
                           breakdown.status === 'closed' ? 'Fechada' : 'Aberta'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {breakdown.status === 'open' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleResolveBreakdown(breakdown.id)}
                          >
                            <CheckCircle2 className="h-4 w-4 mr-1" />
                            Resolver
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Temperatures Tab */}
        <TabsContent value="temperatures">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-chivo">Registo de Temperaturas</CardTitle>
              <Button
                onClick={() => {
                  setSelectedMachine(null);
                  setTemperatureDialogOpen(true);
                }}
              >
                <Plus className="h-4 w-4 mr-2" />
                Registar Temperaturas
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data/Hora</TableHead>
                    <TableHead>Máquina</TableHead>
                    <TableHead>Molde (Fixa/Móvel)</TableHead>
                    <TableHead>Cilindro (Z1-Z4)</TableHead>
                    <TableHead>Bico</TableHead>
                    <TableHead>Óleo</TableHead>
                    <TableHead>Operador</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {temperatureRecords.map((record) => (
                    <TableRow key={record.id}>
                      <TableCell>
                        {new Date(record.recorded_at).toLocaleString('pt-PT')}
                      </TableCell>
                      <TableCell className="font-mono">{record.machine_code}</TableCell>
                      <TableCell>
                        {record.mold_zone_fixed || '-'}°C / {record.mold_zone_mobile || '-'}°C
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {record.cylinder_zone_1 || '-'} | {record.cylinder_zone_2 || '-'} | {record.cylinder_zone_3 || '-'} | {record.cylinder_zone_4 || '-'}
                      </TableCell>
                      <TableCell>{record.nozzle_temp || '-'}°C</TableCell>
                      <TableCell>{record.oil_temp || '-'}°C</TableCell>
                      <TableCell>{record.recorded_by_name}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Checklist Tab */}
        <TabsContent value="checklist">
          <div className="grid md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="font-chivo">Itens do Checklist de Arranque</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {checklistItems.map((item, index) => (
                    <div 
                      key={item.id}
                      className={`p-3 rounded-lg border ${item.is_critical ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="font-mono text-sm text-muted-foreground">{index + 1}</span>
                        <span>{item.description}</span>
                        {item.is_critical && (
                          <Badge className="bg-red-500 ml-auto">Crítico</Badge>
                        )}
                        <Badge variant="outline" className="ml-auto">
                          {item.category === 'safety' ? 'Segurança' :
                           item.category === 'equipment' ? 'Equipamento' :
                           item.category === 'material' ? 'Material' : 'Geral'}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="font-chivo">Últimas Execuções</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {checklistExecutions.slice(0, 10).map((exec) => (
                    <div 
                      key={exec.id}
                      className={`p-3 rounded-lg border ${
                        exec.critical_passed ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="font-bold">{exec.machine_code}</span>
                          <span className="text-sm text-muted-foreground ml-2">
                            {new Date(exec.executed_at).toLocaleString('pt-PT')}
                          </span>
                        </div>
                        {exec.critical_passed ? (
                          <CheckCircle2 className="h-5 w-5 text-green-500" />
                        ) : (
                          <XCircle className="h-5 w-5 text-red-500" />
                        )}
                      </div>
                      <div className="text-sm mt-1">
                        Operador: {exec.operator_name}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Config Dialog */}
      <Dialog open={configDialogOpen} onOpenChange={setConfigDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Configurar Manutenção - {selectedMachine?.code}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Intervalo de Manutenção (horas)</Label>
              <Input
                type="number"
                value={configForm.interval_hours}
                onChange={(e) => setConfigForm({ ...configForm, interval_hours: parseInt(e.target.value) })}
              />
            </div>
            <div>
              <Label>Limiar de Aviso (%)</Label>
              <Input
                type="number"
                step="0.05"
                min="0.5"
                max="0.99"
                value={configForm.warning_threshold}
                onChange={(e) => setConfigForm({ ...configForm, warning_threshold: parseFloat(e.target.value) })}
              />
              <p className="text-xs text-muted-foreground mt-1">0.9 = Aviso a 90% do intervalo</p>
            </div>
            <div>
              <Label>Descrição/Tarefas</Label>
              <Textarea
                value={configForm.description}
                onChange={(e) => setConfigForm({ ...configForm, description: e.target.value })}
                placeholder="Descreva as tarefas de manutenção..."
              />
            </div>
            <Button onClick={handleSaveConfig} className="w-full">Guardar</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Maintenance Record Dialog */}
      <Dialog open={recordDialogOpen} onOpenChange={setRecordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registar Manutenção - {selectedMachine?.code}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Tipo de Manutenção</Label>
              <Select
                value={recordForm.maintenance_type}
                onValueChange={(v) => setRecordForm({ ...recordForm, maintenance_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="preventive">Preventiva</SelectItem>
                  <SelectItem value="corrective">Corretiva</SelectItem>
                  <SelectItem value="emergency">Emergência</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Descrição</Label>
              <Textarea
                value={recordForm.description}
                onChange={(e) => setRecordForm({ ...recordForm, description: e.target.value })}
                placeholder="Descreva o trabalho realizado..."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Duração (minutos)</Label>
                <Input
                  type="number"
                  value={recordForm.duration_minutes}
                  onChange={(e) => setRecordForm({ ...recordForm, duration_minutes: parseInt(e.target.value) })}
                />
              </div>
              <div>
                <Label>Custo (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={recordForm.cost}
                  onChange={(e) => setRecordForm({ ...recordForm, cost: parseFloat(e.target.value) })}
                />
              </div>
            </div>
            <div>
              <Label>Notas</Label>
              <Textarea
                value={recordForm.notes}
                onChange={(e) => setRecordForm({ ...recordForm, notes: e.target.value })}
              />
            </div>
            <Button onClick={handleSaveRecord} className="w-full">Registar Manutenção</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Breakdown Dialog */}
      <Dialog open={breakdownDialogOpen} onOpenChange={setBreakdownDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reportar Avaria</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Máquina</Label>
              <Select
                value={selectedMachine?.id || ""}
                onValueChange={(v) => setSelectedMachine(machines.find(m => m.id === v))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a máquina" />
                </SelectTrigger>
                <SelectContent>
                  {machines.map((m) => (
                    <SelectItem key={m.id} value={m.id}>{m.code} - {m.description}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tipo de Avaria</Label>
              <Select
                value={breakdownForm.breakdown_type}
                onValueChange={(v) => setBreakdownForm({ ...breakdownForm, breakdown_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mechanical">Mecânica</SelectItem>
                  <SelectItem value="electrical">Elétrica</SelectItem>
                  <SelectItem value="hydraulic">Hidráulica</SelectItem>
                  <SelectItem value="pneumatic">Pneumática</SelectItem>
                  <SelectItem value="software">Software</SelectItem>
                  <SelectItem value="other">Outro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Severidade</Label>
              <Select
                value={breakdownForm.severity}
                onValueChange={(v) => setBreakdownForm({ ...breakdownForm, severity: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Baixa</SelectItem>
                  <SelectItem value="medium">Média</SelectItem>
                  <SelectItem value="high">Alta</SelectItem>
                  <SelectItem value="critical">Crítica</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Descrição</Label>
              <Textarea
                value={breakdownForm.description}
                onChange={(e) => setBreakdownForm({ ...breakdownForm, description: e.target.value })}
                placeholder="Descreva a avaria..."
              />
            </div>
            <Button onClick={handleReportBreakdown} className="w-full" disabled={!selectedMachine}>
              Reportar Avaria
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Temperature Dialog */}
      <Dialog open={temperatureDialogOpen} onOpenChange={setTemperatureDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Registar Temperaturas</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Máquina</Label>
              <Select
                value={selectedMachine?.id || ""}
                onValueChange={(v) => setSelectedMachine(machines.find(m => m.id === v))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a máquina" />
                </SelectTrigger>
                <SelectContent>
                  {machines.map((m) => (
                    <SelectItem key={m.id} value={m.id}>{m.code}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Molde Zona Fixa (°C)</Label>
                <Input
                  type="number"
                  value={temperatureForm.mold_zone_fixed}
                  onChange={(e) => setTemperatureForm({ ...temperatureForm, mold_zone_fixed: e.target.value })}
                />
              </div>
              <div>
                <Label>Molde Zona Móvel (°C)</Label>
                <Input
                  type="number"
                  value={temperatureForm.mold_zone_mobile}
                  onChange={(e) => setTemperatureForm({ ...temperatureForm, mold_zone_mobile: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-4 gap-2">
              <div>
                <Label>Cilindro Z1</Label>
                <Input
                  type="number"
                  value={temperatureForm.cylinder_zone_1}
                  onChange={(e) => setTemperatureForm({ ...temperatureForm, cylinder_zone_1: e.target.value })}
                />
              </div>
              <div>
                <Label>Cilindro Z2</Label>
                <Input
                  type="number"
                  value={temperatureForm.cylinder_zone_2}
                  onChange={(e) => setTemperatureForm({ ...temperatureForm, cylinder_zone_2: e.target.value })}
                />
              </div>
              <div>
                <Label>Cilindro Z3</Label>
                <Input
                  type="number"
                  value={temperatureForm.cylinder_zone_3}
                  onChange={(e) => setTemperatureForm({ ...temperatureForm, cylinder_zone_3: e.target.value })}
                />
              </div>
              <div>
                <Label>Cilindro Z4</Label>
                <Input
                  type="number"
                  value={temperatureForm.cylinder_zone_4}
                  onChange={(e) => setTemperatureForm({ ...temperatureForm, cylinder_zone_4: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Bico (°C)</Label>
                <Input
                  type="number"
                  value={temperatureForm.nozzle_temp}
                  onChange={(e) => setTemperatureForm({ ...temperatureForm, nozzle_temp: e.target.value })}
                />
              </div>
              <div>
                <Label>Óleo (°C)</Label>
                <Input
                  type="number"
                  value={temperatureForm.oil_temp}
                  onChange={(e) => setTemperatureForm({ ...temperatureForm, oil_temp: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>Notas</Label>
              <Textarea
                value={temperatureForm.notes}
                onChange={(e) => setTemperatureForm({ ...temperatureForm, notes: e.target.value })}
              />
            </div>
            <Button onClick={handleSaveTemperature} className="w-full" disabled={!selectedMachine}>
              Registar Temperaturas
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
